import { useEffect, useState } from "react";
import useSeo from "@/seo/useSeo";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Users, BedDouble, Bath, Ruler, X, Plus } from "lucide-react";
import { api } from "@/lib/api";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { useI18n } from "@/i18n";
import { photoAlt } from "@/i18n/photoAlts";

export default function GiteDetail() {
  const { slug } = useParams();
  useSeo(`/gite/${slug}`);
  const { t, tg, lang } = useI18n();
  const [raw, setRaw] = useState(null);
  const [range, setRange] = useState({ start: null, end: null });
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setRange({ start: null, end: null });
    api.get(`/gites/${slug}`).then((r) => setRaw(r.data)).catch(() => setRaw(false));
  }, [slug]);

  if (raw === null) return <div className="py-40 text-center text-xs uppercase tracking-[0.3em] text-bone/76">{t("gite.loading")}</div>;
  if (raw === false)
    return <div className="py-40 text-center text-bone/72">{t("gite.notfound")} <Link to="/" className="text-brass">{t("gite.back")}</Link></div>;

  const g = tg(raw);

  return (
    <div data-testid={`gite-page-${slug}`}>
      <section className="relative overflow-hidden border-b border-white/10">
        {g.images.length ? (
          <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 pb-14 pt-12 sm:px-10 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <h1 className="font-display text-5xl tracking-wide text-bone sm:text-6xl lg:text-7xl">
                {g.name}{g.stars ? <span className="align-super text-base tracking-[0.2em] text-brass sm:text-lg"> {"★".repeat(g.stars)}</span> : null}
              </h1>
              <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-bone/72">
                {g.hero_note || t("gite.giteFor", { n: g.capacity })}
              </p>
              {g.short ? <p className="mt-8 max-w-md text-base leading-loose text-bone/70">{g.short}</p> : null}
              <Link to={`/reservation?gite=${g.slug}`} className="btn-primary mt-10 w-fit" data-testid="hero-book-btn">
                {t("gite.bookThis")} <ArrowRight size={15} />
              </Link>
            </div>
            <img src={g.image} alt={photoAlt(lang, g.image, 0)} className="order-1 mx-auto block h-auto w-full max-h-[76vh] object-contain lg:order-2"
              style={{ filter: "brightness(1.18) contrast(1.03)" }} />
          </div>
        ) : (
          <div className="mx-auto max-w-[1400px] px-6 pb-16 pt-14 sm:px-10">
            <div className="relative mx-auto w-fit">
              <img src={g.image} alt={photoAlt(lang, g.image, 0)} className="mx-auto h-48 w-auto object-contain opacity-90 sm:h-64" />
              <span className="absolute inset-x-0 bottom-0 bg-ink/70 py-2 text-center text-[10px] uppercase tracking-[0.34em] text-brass">
                {t("gite.soonBadge")}
              </span>
            </div>
            <div className="mt-12">
              <h1 className="font-display text-5xl tracking-wide text-bone sm:text-6xl lg:text-7xl">
                {g.name}{g.stars ? <span className="align-super text-base tracking-[0.2em] text-brass sm:text-lg"> {"★".repeat(g.stars)}</span> : null}
              </h1>
              <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-bone/72">
                {g.hero_note || t("gite.giteFor", { n: g.capacity })}
              </p>
              <p className="mt-4 text-[11px] uppercase tracking-[0.34em] text-brass">{t("gite.upcoming")}</p>
            </div>
          </div>
        )}
      </section>

      <div className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10">
        <div className="grid gap-20 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">{t("gite.lodging")}</p>
            <p className="mt-7 text-base leading-loose text-bone/72">{g.description}</p>

            <div className="mt-12 grid grid-cols-2 gap-6 border-y border-white/10 py-8 sm:grid-cols-4">
              {[
                { i: Users, l: t("gite.travelers", { n: g.capacity }) },
                { i: BedDouble, l: t("gite.bedsLine", { r: g.bedrooms, b: g.beds }) },
                { i: Bath, l: t("gite.bathroom", { n: g.baths }) },
                { i: Ruler, l: `${g.surface} m²` },
              ].map((x) => (
                <div key={x.l}>
                  <x.i size={16} className="text-brass" />
                  <p className="mt-3 text-[11px] uppercase tracking-[0.16em] text-bone/72">{x.l}</p>
                </div>
              ))}
            </div>

            <p className="eyebrow mt-16">{t("gite.amenities")}</p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {g.amenities.map((a) => (
                <li key={a} className="flex items-start gap-3 text-sm text-bone/72">
                  <Check size={15} className="mt-0.5 shrink-0 text-brass" /> {a}
                </li>
              ))}
            </ul>

            <p className="eyebrow mt-16">{t("gite.good2know")}</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="gite-rules">
              {g.rules.map((r) => (
                <div key={r} className="border border-white/10 bg-panel px-5 py-6 text-sm leading-relaxed text-bone/72">
                  {r}
                </div>
              ))}
            </div>

            <p className="eyebrow mt-16">{t("gite.gallery")}</p>
            {g.images.length ? (
              <div className="mt-8 grid grid-cols-2 gap-1 sm:grid-cols-3">
                {g.images.map((url, i) => (
                  <button key={url} onClick={() => setLightbox(url)} data-testid={`gite-img-${i}`} className="img-hover aspect-square">
                    <img src={url} alt={photoAlt(lang, url, i)} loading="lazy" decoding="async" className="h-full w-full object-cover"
                      style={{ filter: "brightness(1.16) contrast(1.05)" }} />
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-8 border border-white/10 bg-panel px-6 py-16 text-center" data-testid="gallery-soon">
                <img src={g.image} alt={photoAlt(lang, g.image, 0)} className="mx-auto h-40 w-auto object-contain opacity-80" />
                <p className="mt-8 text-[10px] uppercase tracking-[0.34em] text-brass">{t("gite.upcoming")}</p>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-bone/76">{t("gite.galleryWait")}</p>
              </div>
            )}

            {g.active && (
              <div className="mt-20" data-testid="gite-faq">
                <p className="eyebrow">{t("faq.title")}</p>
                <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => (
                    <details key={n} className="group py-5" data-testid={`faq-${n}`}>
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-sm text-bone marker:content-none">
                        <span>{t(`faq.q${n}`)}</span>
                        <Plus size={15} className="shrink-0 text-brass transition-transform duration-300 group-open:rotate-45" />
                      </summary>
                      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                        {t(`faq.a${n}`)}
                      </p>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {g.active && (
              <div className="mt-20">
                <p className="eyebrow">{t("gite.availability")}</p>
                <h2 className="mt-6 font-display text-4xl tracking-wide text-bone">{t("gite.calendar")}</h2>
                <div className="panel mt-8 p-6 sm:p-8">
                  <AvailabilityCalendar giteSlug={slug} range={range} onSelect={setRange} months={2} />
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-5">
            <div className="panel sticky top-32 p-9">
              {g.active ? (
                <>
                  <p className="eyebrow">{t("gite.availability")}</p>
                  <p className="mt-4 font-display text-3xl leading-tight text-bone">{t("gite.rates")}</p>
                  <p className="mt-3 text-xs leading-relaxed text-bone/76">{t("gite.priceNote")}</p>
                </>
              ) : (
                <>
                  <p className="eyebrow">{t("gite.opening")}</p>
                  <p className="mt-4 font-display text-4xl text-bone">{t("gite.soonAvailable")}</p>
                  <p className="mt-3 text-xs leading-relaxed text-bone/76">{t("gite.openNote")}</p>
                </>
              )}
              <div className="rule my-8" />
              {g.active ? (
                <Link
                  to={`/reservation?gite=${slug}${range.start ? `&start=${range.start}` : ""}${range.end ? `&end=${range.end}` : ""}`}
                  className="btn-primary w-full"
                  data-testid="gite-book-btn"
                >
                  {t("gite.bookThis")} <ArrowRight size={15} />
                </Link>
              ) : (
                <Link to="/contact" className="btn-primary w-full" data-testid="gite-contact-btn">
                  {t("gite.notifyMe")}
                </Link>
              )}
              <ul className="mt-8 space-y-3 text-[11px] uppercase tracking-[0.16em] text-bone/75">
                <li>{t("gite.secure")}</li>
                <li>{t("gite.towels")}</li>
                <li>{t("gite.checkin")}</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-ink/95 p-6"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <button className="absolute right-8 top-8 text-bone/72 hover:text-brass" onClick={() => setLightbox(null)}>
            <X size={26} />
          </button>
          <img src={lightbox} alt={photoAlt(lang, lightbox, g.images.indexOf(lightbox))} className="max-h-[86vh] max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}
